import { useEffect, useState } from "react";
import { Github, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const TOKEN_URL = "https://github.com/settings/tokens/new?scopes=repo&description=SYNAPSIS";
const RESULT = {
  connected: ["success", "Cuenta de GitHub conectada"],
  denied: ["error", "Cancelaste la autorización en GitHub"],
  missing_scope: ["error", "GitHub no concedió permiso sobre repositorios. Intenta de nuevo y acepta los permisos"],
  invalid_state: ["error", "La solicitud de conexión expiró. Intenta de nuevo"],
  error: ["error", "No se pudo completar la conexión con GitHub. Intenta de nuevo"],
};
const Step = ({ n, children }) => (
  <div className="flex items-start gap-3"><span className="w-5 h-5 shrink-0 rounded-full bg-blue-500/20 text-blue-300 text-[11px] grid place-items-center">{n}</span><div className="flex-1 space-y-1.5">{children}</div></div>
);

function Connect({ configured, onConnected }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const oauth = async () => {
    setBusy(true);
    try { const { data } = await api.get("/github/oauth/start", { params: { return_to: window.location.pathname } }); window.location.assign(data.url); }
    catch (e) { toast.error(formatError(e)); setBusy(false); }
  };
  const withToken = async () => {
    setBusy(true);
    try { const { data } = await api.post("/github/connect", { token: token.trim() }); setToken(""); toast.success(`Conectado como ${data.login}`); onConnected(data.login); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  if (configured) return (
    <div className="mt-2 space-y-2">
      <p className="text-xs text-slate-500">Autoriza a SYNAPSIS en tu cuenta de GitHub para crear repositorios y subir el código.</p>
      <button onClick={oauth} disabled={busy} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-white text-[#0A0D14] font-medium hover:bg-slate-200 disabled:opacity-40" data-testid="github-oauth-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Github className="w-3.5 h-3.5" />}Conectar con GitHub
      </button>
    </div>
  );
  return (
    <div className="mt-3 space-y-3" data-testid="github-token-flow">
      <Step n={1}>
        <p className="text-xs text-slate-300">Crea una llave de acceso en tu cuenta de GitHub (ya viene con el permiso correcto). Elige la duración, baja y pulsa «Generate token».</p>
        <a href={TOKEN_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-white text-[#0A0D14] font-medium hover:bg-slate-200" data-testid="github-create-token-link"><Github className="w-3.5 h-3.5" />Crear llave en GitHub<ExternalLink className="w-3 h-3" /></a>
      </Step>
      <Step n={2}>
        <p className="text-xs text-slate-300">Copia la llave que te muestra GitHub, pégala aquí y conecta. Se guarda cifrada y solo se usa para tus proyectos.</p>
        <div className="flex gap-2">
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="ghp_…" className="h-8 bg-[#0A0D14] font-mono text-xs" data-testid="github-token-input" />
          <button onClick={withToken} disabled={busy || token.trim().length < 20} className="text-xs px-3 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-40 text-white inline-flex items-center gap-1" data-testid="github-connect-btn">{busy && <Loader2 className="w-3 h-3 animate-spin" />}Conectar</button>
        </div>
      </Step>
    </div>
  );
}

export function GitHubPanel({ project, version }) {
  const [st, setSt] = useState(null);
  const [repo, setRepo] = useState((project.github?.repo || "").split("/")[1] || project.name.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "mi-app");
  const [priv, setPriv] = useState(true);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(project.github);
  useEffect(() => {
    api.get("/github/status").then((r) => setSt(r.data)).catch((e) => toast.error(formatError(e)));
    const q = new URLSearchParams(window.location.search);
    const res = RESULT[q.get("github")];
    if (res) { toast[res[0]](res[1]); q.delete("github"); window.history.replaceState(null, "", window.location.pathname + (q.toString() ? `?${q}` : "")); }
  }, []);
  const disconnect = () => api.delete("/github/connect").then(() => { setSt({ ...st, connected: false, login: null }); toast.success("GitHub desconectado"); });
  const push = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/projects/${project.id}/github/push`, { repo, private: priv, version }); setLast({ url: data.url, version: data.version }); toast.success(`v${data.version} guardada en GitHub`); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  if (!st) return null;
  return (
    <div className="rounded-xl border border-[#222B40] p-4" data-testid="github-panel">
      <h3 className="text-white text-sm font-medium flex items-center gap-2"><Github className="w-4 h-4" />Guardar código en GitHub</h3>
      {!st.connected ? <Connect configured={st.configured} onConnected={(login) => setSt({ ...st, connected: true, login })} /> : (
        <>
          <p className="text-xs text-emerald-300 mt-1" data-testid="github-connected">Conectado como {st.login} <button onClick={disconnect} className="ml-2 text-slate-500 hover:text-red-300" data-testid="github-disconnect-btn">desconectar</button></p>
          <div className="flex flex-wrap gap-2 mt-3 items-center">
            <span className="text-xs text-slate-500">{st.login}/</span>
            <Input value={repo} onChange={(e) => setRepo(e.target.value)} className="h-8 w-48 bg-[#0A0D14] font-mono text-xs" data-testid="github-repo-input" />
            <label className="text-xs text-slate-300 flex items-center gap-1"><input type="checkbox" checked={priv} onChange={(e) => setPriv(e.target.checked)} data-testid="github-private-checkbox" />Privado</label>
            <button onClick={push} disabled={busy || !version} className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 disabled:opacity-40 text-white inline-flex items-center gap-1" data-testid="github-push-btn">{busy && <Loader2 className="w-3 h-3 animate-spin" />}Guardar v{version}</button>
          </div>
        </>
      )}
      {last?.url && <a href={last.url} target="_blank" rel="noreferrer" className="block mt-2 text-xs text-cyan-400" data-testid="github-repo-link">{last.url} (v{last.version})</a>}
      <p className="text-[11px] text-slate-600 mt-2">Disponible desde el Plan Profesional. Se suben src/, dist/index.html y README.md.</p>
    </div>
  );
}
